export interface TradeSizeLimits {
  minNotional?: number;
  minQty?: number;
  quantoMultiplier?: number;
  markPrice?: number;
  contractValue?: number;
}

/**
 * Smallest position value in USDT.
 * Contract venues (Gate) size by contract value, not by treating minQty as BTC.
 * Linear venues (KuCoin, Binance, Bybit) size by minQty times price when minQty is a fraction of one coin.
 */
export function minimumPositionNotional(
  limits: TradeSizeLimits | null | undefined,
  lastPrice?: number
): number {
  if (!limits) return 0;
  const stated = limits.minNotional ?? 0;
  const price =
    lastPrice && lastPrice > 0
      ? lastPrice
      : limits.markPrice && limits.markPrice > 0
        ? limits.markPrice
        : 0;
  const minQty = limits.minQty ?? 0;

  if (limits.contractValue && limits.contractValue > 0 && minQty > 0) {
    return Math.max(stated, limits.contractValue * minQty);
  }
  if (
    limits.quantoMultiplier &&
    limits.quantoMultiplier > 0 &&
    minQty > 0 &&
    price > 0
  ) {
    return Math.max(stated, minQty * limits.quantoMultiplier * price);
  }
  if (minQty > 0 && minQty < 1 && price > 0) {
    return Math.max(stated, minQty * price);
  }
  return stated;
}

export function belowMinimumTradeMessage(
  notional: number,
  leverage: number
): string {
  const margin = minimumTradeMargin(notional, leverage);
  return `This amount is too low. The position must be worth at least ${notional.toFixed(2)} USDT (${margin} USDT margin at ${leverage}x).`;
}

/** Margin the form will accept, rounded up to the next whole dollar (trades are whole USDT). */
export function minimumTradeMargin(notional: number, leverage: number): number {
  if (
    !Number.isFinite(notional) ||
    !Number.isFinite(leverage) ||
    notional <= 0 ||
    leverage <= 0
  ) {
    return 0;
  }
  return Math.ceil(Number((notional / leverage).toFixed(6)));
}

const GENERIC_TRADE_FAILURE =
  /^(Failed to open position on exchange|Failed to open trade|Failed to close position on exchange|Something went wrong\. Please try again\.?|Something went wrong\. Please try again in a moment\.|The exchange didn't accept this order\.(?: Please try again in a moment\.)?)$/;

export function isGenericTradeFailure(message: string): boolean {
  return GENERIC_TRADE_FAILURE.test(message.trim());
}

/**
 * Keep a generic exchange failure only when the form cannot explain it.
 * A size below the contract minimum is explained from the limits we already have.
 */
export function specificOpenError(
  message: string,
  input: { amount: number; leverage: number; positionNotional: number }
): string {
  if (!isGenericTradeFailure(message)) return message;
  const { amount, leverage, positionNotional } = input;
  if (
    positionNotional > 0 &&
    leverage > 0 &&
    amount * leverage < positionNotional
  ) {
    return belowMinimumTradeMessage(positionNotional, leverage);
  }
  return message;
}
